import type { ExpenseCategoryValue, ExpenseSummaryDto, MemberDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { RankedBarChart, type RankedBar } from "@gladis/ui";
import { expenseCategoryLabel, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { SectionCard } from "../section-card.js";
import { filterScope, type HealthFilterState } from "./health-filters.js";

/**
 * ⑤ Where the money goes — the ten largest categories, as a ranking.
 *
 * ==========================================================================================
 * A RANKED BAR, AND THE SCALE IS WHY IT IS ALLOWED TO BE A MARK AT ALL
 * ==========================================================================================
 *
 * These were ten text rows, and `dashboard.md`'s primitive table said why: "A ranking, and no ranked
 * primitive exists". One does now, and the argument it was blocked on is unchanged and still binding.
 *
 * The rows are the ten largest categories of up to seventeen, with no "Other" bucket and no remainder
 * row, so they do NOT add up to the headline. FR-067 forbids a percentage, a bar, a share or any
 * part-of-whole mark beside them for that reason — a proportion of a whole the rows do not constitute is
 * a claim the reader cannot check.
 *
 * `RankedBarChart` is exempt from that ONLY BECAUSE ITS AXIS IS THE LARGEST BAR, never the sum. Scaled
 * to the sum it would be a pie in different coordinates, with the extra tell that the bars visibly fail
 * to fill the axis. `ranked.ts` carries the argument beside the one line that implements it, and a test
 * fails if that line changes.
 *
 * WHAT DID NOT CHANGE: the hint still says what the ranking is NOT. A reader adding ten bars and finding
 * they fall short of the headline would conclude a figure is wrong, and stating it is the honest version
 * of the percentage this card refuses to draw. It matters more with a mark than it did with rows,
 * because a bar chart looks like it ought to add up.
 *
 * ==========================================================================================
 * FULL WIDTH, BENEATH BOTH FLOW SECTIONS
 * ==========================================================================================
 *
 * It is the detail behind `Out`, so it belongs to neither of the two sections above it exclusively, and
 * ten labelled rows want the horizontal room — more so as a chart, whose category labels are words on
 * the y-axis. The card takes the width it is given; the page decides that.
 */

/** Cents-as-string → euros. Parsed once, at the point of formatting. */
function euros(cents: string): number {
  return Number(cents) / 100;
}

export function ExpenseSummaryCard({
  summary,
  filters,
  members,
}: {
  summary: ExpenseSummaryDto;
  filters: HealthFilterState;
  members: readonly MemberDto[];
}): ReactNode {
  const t = useT();
  const bars: RankedBar[] = summary.categories.map((row) => ({
    key: row.category,
    /*
     * The label comes from the shared catalogue rather than from the payload, so there is one place the
     * seventeen names live — the same lookup every picker and chip in the application uses. The cast is
     * the boundary between a string on the wire and the catalogue's union; a category the catalogue does
     * not know falls back to its own value rather than rendering blank.
     */
    label: expenseCategoryLabel(row.category as ExpenseCategoryValue),
    value: euros(row.monthlyCents),
  }));

  return (
    <SectionCard
      title={t("dash.ranked.heading")}
      value={formatEur(euros(summary.totalCents))}
      hint={
        bars.length === 0
          ? t("dash.ranked.empty")
          : /*
             * `filterScope` AND NOT `filterSummary`, because this section is "per month" by name and
             * follows the scope only. It used to take the whole phrase and strip the period back off
             * with a regular expression anchored on the English words, which produced "Household, this
             * year, this month" the moment either half changed and would have failed silently in Dutch.
             */
            t("dash.ranked.hint", { who: filterScope(filters, members) })
      }
      testId="expense-summary-card"
      valueTestId="expense-summary-total"
    >
      <RankedBarChart bars={bars} emptyMessage={t("dash.ranked.empty")} />
    </SectionCard>
  );
}
